/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import getConfig from '../config';
import {
	APPS_TO_CODES_MAPPING,
	APP_TYPES,
	ASSETS,
	CCEXPRESS_TEXT_TYPE_MAPPER,
	CCEXPRESS_TYPE_MAPPER,
	CLOUD_DOC_TYPE_MAPPER,
	DEFAULT_ASSET_ICONS,
	GENERIC_ASSET_ICONS,
	MC,
	SEARCH_CCD_CCH_SAMCAP_CGEN,
	SEARCH_CCD_COMPONENTS,
	SEARCH_FILTERS_CATEGORY_LABELS,
	SEARCH_ICONS,
	SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS,
	SEARCH_TABS_CONSTANTS,
	SEARCH_YOUR_WORK_FILTERS,
	SPARK,
	SPARK_TEXT_TYPE_MAPPER,
	SPARK_TYPE_MAPPER,
	STORAGE_CONSTANTS,
	TEMPLATE_TYPES,
	TOP_CC_QUERIES,
	TOP_PS_QUERIES,
	UI_THEMES,
	XD_TYPE_MAPPER,
} from '../containers/SearchResultsPage/SearchConstants';
import filesMap from '../constants/MimeTypeConstants';
import GlobalsUtils from './GlobalsUtils';
import { getLogger } from './SearchLogger';
import WebStorage from '../storage/WebStorage';
import SearchHostContext from './SearchHostContext';
import { Utils } from '../utils/Globals';
import SearchService from '../services/SearchService';
import FileTypeService from '../services/FileTypeService';
import FloodgateService from '../services/FloodgateService';
import SearchConfig from './SearchConfig';
import { getLocalizedURL, normalize } from '@intl/adobe-locales';
import uuid from 'uuid/v4';
import SearchAnalytics from '../analytics/SearchAnalytics';
import Trie from './Autocomplete/Trie';
import ConfigService from '../services/ConfigService';
import store from '../store';
import { FETCH_SEARCH_APP_METADATA } from '../actionTypes';
import RenditionService from '../services/RenditionService';
import UserPreferencesModel from '../personalization/UserPreferencesModel';
export default class SearchUtils {
	static didResetSearchBar: boolean;
	static localFiles: string;
	static host: any;
	static searchAutoCompleteData: Array<AutoCompleteData>;
	static searchQueryIntentsMap: Record<string, Array<Intent>>;
	static dictonaries: Record<string, Trie | null> = {
		searchIntentDictionary: null,
	};

	/**
	 * It takes in an array of appIds and an object of app metadata and returns the icon url of the first
	 * appId in the array that has a corresponding app metadata object
	 * @param  - `appIdList` - The list of app IDs that are associated with the search result.
	 * @returns The icon url of the first app in the list of appIds.
	 */
	static getAppIcon({
		sapCodes,
		searchAppMetadataList,
		defaultIcon,
	}: {
		sapCodes: string[] | string;
		searchAppMetadataList: AppMetadata;
		defaultIcon?: string;
	}): string {
		let icon = defaultIcon ?? APPS_TO_CODES_MAPPING.default_offline;

		try {
			if (Array.isArray(sapCodes)) {
				for (const sapCode of sapCodes) {
					if (searchAppMetadataList.apps[sapCode]) {
						icon = SearchUtils.getAppIconUrl(searchAppMetadataList.apps[sapCode].iconUrls);
						break;
					}
				}
			} else {
				icon = SearchUtils.getAppIconUrl(searchAppMetadataList.apps[sapCodes].iconUrls);
			}

			return icon;
		} catch (err) {
			return icon;
		}
	}

	/**
	 * [getApps gets app the apps matching the name from QIS]
	 * @param  {HostContext} searchHostContext
	 * @param  {[string]} name
	 * @returns {AppResultsArray}
	 */
	static getAutoCompleteQuickLinkApps(
		searchHostContext: HostContext,
		appMetadata: AppMetadata,
		searchAutoCompleteItems: Record<string, any>,
	): Array<AppResultItem> {
		let appCodesFromAutoComplete: string[] = [];
		const sapCodesFromProductIntents: Array<Array<string>> = [];
		searchAutoCompleteItems.forEach((searchServerSearchAutoCompleteItem) => {
			if (searchServerSearchAutoCompleteItem) {
				const sapCode = searchServerSearchAutoCompleteItem.sapCode;
				const ffcId = searchServerSearchAutoCompleteItem.ffcId;
				sapCode && (appCodesFromAutoComplete = appCodesFromAutoComplete.concat(sapCode.split(',')));
				ffcId && (appCodesFromAutoComplete = appCodesFromAutoComplete.concat(ffcId.split(',')));

				const productIntents = searchServerSearchAutoCompleteItem?.product_intents || [];
				productIntents.forEach((productIntentItem, index) => {
					!sapCodesFromProductIntents[index] && (sapCodesFromProductIntents[index] = []);
					productIntentItem.product_code &&
						sapCodesFromProductIntents[index].push(productIntentItem.product_code);
				});
			}
		});

		const useAppsFromIntentModel = SearchUtils.isFloodgateFeatureInTest(
			'com.adobe.uss.ccs.appIntent',
			searchHostContext,
		);

		const appCodeSet = new Set([
			...(useAppsFromIntentModel ? sapCodesFromProductIntents.flat() : appCodesFromAutoComplete),
		]); // contains either sapcode or ffcid
		let appResultsArray: any = [];

		for (const id of appCodeSet) {
			if (appMetadata.apps[id]) {
				const quickLinkApp: AppResultItem = { ...appMetadata.apps[id] };
				quickLinkApp.renderType =
					SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_QUICK_LINK as AutoCompleteAppRenderType;
				quickLinkApp.appType = APP_TYPES.APPS as SearchDropdownItemTypes;
				quickLinkApp.sapCode = quickLinkApp.ffcId = id;
				appResultsArray.push(quickLinkApp);
			}
		}

		// If the client is CCDesktop, we want to suppress the Creative Cloud app result.
		if (searchHostContext.hostInfo.surface === 'CCDesktop' && searchHostContext.hostInfo.client === 'CCDesktop') {
			appResultsArray = appResultsArray.filter((app) => typeof app.id === 'string' && app.id !== 'KCCC');
		}

		return appResultsArray;
	}

	/**
	 * [createIntentDictionary creates a dictionary for a set of intents]
	 * @param  {[string]} intents
	 */
	static createIntentDictionary(intents: Intent[] = []): void {
		const searchHostContext = SearchHostContext.getContext();
		const distinctIntentWords: Set<string> = new Set();
		let queryIntentMap: Record<string, Intent[]> = {};
		const I2ASessionData = (WebStorage.sessionStorage.getItem('asde.autocomplete.I2A') ?? {}) as Record<
			string,
			any
		>;
		queryIntentMap = intents?.reduce((acc, intent) => {
			const enIntentQueries = intent?.query?.split(/\s+/).map((q) => q.toLowerCase());
			const localizedIntentQueries: string[] = intent?.query?.split(/\s+/)?.reduce((queries, q) => {
				const translatedQueryString = FileTypeService.fileTypeDataObject?.translations?.intents?.[q] ?? '';
				if (translatedQueryString) {
					const translatedIntentQueries: string[] = translatedQueryString
						.split(/\s+/)
						.map((translatedQuery) =>
							translatedQuery
								.normalize('NFD')
								.toLocaleLowerCase(normalize(searchHostContext.locale ?? 'en-US')),
						);
					queries.push(...translatedIntentQueries);
				}
				return queries;
			}, []);
			const intentQueries = new Set([...localizedIntentQueries, ...enIntentQueries]);
			for (const query of intentQueries) {
				acc[query] = [...(acc?.[query] ?? []), intent];
				distinctIntentWords.add(query);
			}
			return acc;
		}, {});
		const I2ADictonary = GlobalsUtils.createDictionary([...distinctIntentWords], searchHostContext);
		I2ASessionData.sessionDictionary = {};
		I2ASessionData.sessionQueryWords = [...distinctIntentWords].join(' ');
		I2ASessionData.queries = {};
		WebStorage.sessionStorage.setItem('asde.autocomplete.I2A', I2ASessionData);
		SearchUtils.dictonaries.searchIntentDictionary = I2ADictonary;
		SearchUtils.searchQueryIntentsMap = queryIntentMap;
	}

	/**
	 * [getAppsForIntentShortcuts gets apps for the given intents]
	 * @param  {HostContext} searchHostContext
	 * @param  {[string]} name
	 * @returns {AppResultsArray}
	 */
	static getAppsForIntentShortcuts(appMetadata: AppMetadata, intents: Intent[] = []): Array<AppResultItem> {
		const intentsNormalized: Record<string, Intent> = intents.reduce(
			(acc, intent) => ({ ...acc, [intent.id]: intent }),
			{},
		);
		const intentIds: string[] = Object.keys(intentsNormalized);
		const distinctIntentIds = new Set([...intentIds]); // contains intent ids

		const appResultsArray: any = [];

		for (const id of distinctIntentIds) {
			const intent = intentsNormalized[id];
			const sapCode = intent.sapCode;
			const ffcId = intent.ffcId;
			const iconUrls = [];
			intent.icon && iconUrls.push(intent.icon);
			intent.iconDark && iconUrls.push(intent.iconDark);
			let app: any = {};
			if (appMetadata.apps[sapCode || ffcId]) {
				app = { ...appMetadata.apps[sapCode || ffcId] };
				app.actionLabel = intent.action?.label || app.actionLabel;
				app.actionType = intent.action?.type || app.actionType;
				app.url = !SearchHostContext.isAuthenticated() && intent.action?.url ? intent.action.url : app.url;
				app.description = intent.description || app.description;
				app.sapCode = sapCode;
				app.ffcId = ffcId;
			} else if (!sapCode && !ffcId) {
				app = { ...intent };
				app.actionLabel = intent.action.label;
				app.actionType = intent.action.type;
				app.url = intent.action.url;
				app.description = intent.description || '';
				app.iconUrls = iconUrls.length > 0 ? iconUrls : [];
				app.platforms = [];
				app.actionDeeplinkParams = intent.action.deeplinkParams;

				delete app.action;
				delete app.icon;
				delete app.disabled;
			} else {
				return appResultsArray;
			}

			app.id = intent.id;
			app.score = intent.score;
			app.url = getLocalizedURL(app.url, normalize(SearchHostContext.getContext().locale, 'ccsearch'));
			app.renderType = SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_SHORTCUT;
			app.appType = APP_TYPES[intent.type && intent.type.toUpperCase()] || intent.type;
			app.actionLabel =
				FileTypeService.fileTypeDataObject.translations.intents[app.actionLabel] || app.actionLabel;
			app.actionDeeplinkParams =
				FileTypeService.fileTypeDataObject.translations.intents[app.actionDeeplinkParams] ||
				app.actionDeeplinkParams;
			app.actionOpenUrlInSameTab = intent?.action?.sameTab ?? false;
			app.description =
				FileTypeService.fileTypeDataObject?.translations?.intents?.[app.descriptionL10n || app.description] ??
				app.description;
			app.name =
				FileTypeService.fileTypeDataObject?.translations?.intents?.[app.nameL10n || app.name] ?? app.name;
			appResultsArray.push(app as AppResultItem);
		}

		return appResultsArray;
	}

	/**
	 * [escapeRegExSpecialCharacters escapes RegEx special characters]
	 * @param  {[string]} query
	 * @returns {[string]}
	 */
	static escapeRegExSpecialCharacters(query: string): string {
		return query.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&'); // eslint-disable-line no-useless-escape
	}

	/**
	 * [getTemplateTypes get ccexpress code names]
	 * @param  {[string]} type
	 * @returns {[string]}
	 */
	static getTemplateTypes(type: string): string {
		switch (type) {
			case TEMPLATE_TYPES.PS:
				return 'app.search.template.type.photoshop';

			case TEMPLATE_TYPES.IN:
				return 'app.search.template.type.indesign';

			case TEMPLATE_TYPES.ILLUS:
				return 'app.search.template.type.illustrator';

			default:
				return '';
		}
	}

	/**
	 * [checkRenditionIsValid checks to see if renditions returns 404]
	 * The file could be from the ccx-start.
	 * If that is the case we will already have an image
	 * blob.
	 * @param  {[string]} imageUrl
	 * @returns {[string or null]}
	 */
	static checkRenditionIsValid(imageUrl: string): Promise<boolean> {
		return new Promise((resolve) => {
			// image blob
			if (imageUrl && imageUrl.includes('data:image')) {
				resolve(true);
			} else {
				const img = new Image();

				img.onload = () => resolve(true);

				img.onerror = () => resolve(false);

				img.src = imageUrl;
			}
		});
	}

	/**
	 * [getSparkCodeName get ccexpress code names]
	 * @param  {[string]} mimeType
	 * @returns {[string or null]}
	 */
	static getSparkCodeName(mimeType: string): string {
		switch (mimeType) {
			case SPARK.POST:
				return 'adobe-theo';

			case SPARK.VIDEO:
				return 'adobe-voice';

			case SPARK.PAGE:
				return 'slate';

			default:
				return '';
		}
	}

	/**
	 * [sparkTypeToText converts the ccexpress type to text]
	 * @param  {[string]} sparkType
	 * @returns {[string]}
	 */
	static sparkTypeToText(params: { template: boolean; sparkType?: string }): string {
		const { template, sparkType } = params;
		let localizedString = this.isSparkRebrandingEnabled()
			? 'app.search.ccexpress.type.post'
			: 'app.search.spark.type.post';

		if (sparkType !== undefined && sparkType !== null) {
			if (template) {
				localizedString = this.isSparkRebrandingEnabled()
					? CCEXPRESS_TEXT_TYPE_MAPPER[sparkType]
					: SPARK_TEXT_TYPE_MAPPER[sparkType];
			} else {
				localizedString = this.isSparkRebrandingEnabled()
					? CCEXPRESS_TYPE_MAPPER[sparkType]
					: SPARK_TYPE_MAPPER[sparkType];
			}

			if (!localizedString) {
				localizedString = this.isSparkRebrandingEnabled()
					? 'app.search.ccexpress.type.post'
					: 'app.search.spark.type.post';
			}
		}

		return localizedString;
	}

	/**
	 * [xdTypeToText converts the xd type to text]
	 * @param  {[string]} xdType
	 * @returns {[string]}
	 */
	static xdTypeToText(xdType: string) {
		let string = XD_TYPE_MAPPER[xdType];

		if (!string) {
			string = 'app.type.unknown';
		}

		return string;
	}

	/**
	 * [cloudDocType converts the xd type to text]
	 * @param  {[string]} type
	 * @returns {[string]}
	 */
	static cloudDocType(type?: string) {
		let string = CLOUD_DOC_TYPE_MAPPER[type as string];

		if (!string) {
			string = 'app.type.unknown';
		}

		return string;
	}

	/**
	 * [lightRoomSourceURLFixer fixes incorrect sourceURL from USS]
	 * @param  {[string]} url
	 * @returns {[string]}
	 */
	static lightRoomSourceURLFixer(env: Environments, url: string) {
		const lightRoomSourceId = url ? url.split('catalogs')[1] : '';
		return `${getConfig(env, 'LIGHTROOM_BASE_SOURCE_URL')}${lightRoomSourceId}`;
	}

	/**
	 * [getUseCaseForSparkTemplates returns the first use case or the use case that mataches query]
	 * @param  {[Array]} usecase
	 * @returns {[string]}
	 */
	static getUseCaseForSparkTemplates(usecase: Array<string>) {
		const currentQueryArray = this.getCurrentQuery().split(' ');
		const getMatchingUseCase = usecase.filter((element) => currentQueryArray.includes(element.toLowerCase()));

		if (getMatchingUseCase[0]) {
			return getMatchingUseCase[0];
		}

		return usecase[0];
	}

	/**
	 * [getSparkRedirectUrl provides the url to redirect user to spark site]
	 * @param  {[string]} type
	 * @param  {[string]} sparkID
	 * @returns {[string]}
	 */
	static getSparkRedirectUrl(env: Environments, type: string, sparkID: string): string | null {
		const sparkBaseURLID = SearchUtils.getIDForSparkBaseURL();
		const getSparkBaseUrl = getConfig(env, sparkBaseURLID);

		switch (type) {
			case SPARK.POST:
				return `${getSparkBaseUrl}/post/${sparkID}`;

			case SPARK.VIDEO:
				return `${getSparkBaseUrl}/video/${sparkID}`;

			case SPARK.PAGE:
				return `${getSparkBaseUrl}/page/${sparkID}`;

			default:
				return null;
		}
	}

	/**
	 * [sparkTemplatesRenditionUrl replaces the format, dimension and size with desired values from the ccexpress templates rendition url]
	 * @param  {[string]} renditionUrl
	 * @returns {[string]}
	 */
	static sparkTemplatesRenditionUrl(renditionUrl: string): string {
		return renditionUrl.replace('{format}', 'jpg').replace('{dimension}', 'width').replace('{size}', '200');
	}

	/**
	 * [getMCRendtionURL provides the  rendition url for MC]
	 * @param  {[string]} type
	 * @param  {[string]} mcID
	 * @returns {[string]}
	 */
	static getMCRenditionURL(
		env: Environments,
		type: string | null | undefined,
		mcID: string | null | undefined,
		path: Array<string>,
		parentId: string | null | undefined,
	): string {
		const getMCBaseUrl = getConfig(env, 'MC_BASE_URL');
		const getMCID = String(mcID);

		switch (type) {
			case MC.MIX:
				return `${getMCBaseUrl}/adobe-psmix/${getMCID}`;

			case MC.SKETCH:
				return `${getMCBaseUrl}/adobesketch/${getMCID}`;

			case MC.FIX:
				return `${getMCBaseUrl}/adobe-psfix/${getMCID}`;

			case MC.DRAW:
				return `${getMCBaseUrl}/adobedraw/${getMCID}`;

			case MC.COMP:
				return `${getMCBaseUrl}/adobe-layup/${getMCID}`;

			case MC.CLIP:
				return `${getMCBaseUrl}/adobe-premiereclip/${getMCID}`;

			case MC.CLS:
				if (path && parentId) {
					return `${getMCBaseUrl}/adobeclsmix/${getMCID}`;
				}

				return `${getMCBaseUrl}/adobe-psfix-cls/${getMCID}`;

			default:
				return '';
		}
	}

	/**
	 * [getGenericRenditions converts types to generic icon rendition url]
	 * @param  {[string]} type
	 * @param {[string]} theme OPTIONAL. Default is light theme.
	 * @returns {[string]}
	 */
	static getGenericRenditions(type: string, theme?: string) {
		switch (type) {
			case ASSETS.FOLDER:
			case ASSETS.SHAREDFOLDER:
			case ASSETS.XDV1:
			case ASSETS.XDV2:
			case ASSETS.PDF:
			case ASSETS.PSD:
			case ASSETS.AI:
			case ASSETS.SVG:
			case ASSETS.SPARKPOST:
			case ASSETS.SPARKPAGE:
			case ASSETS.SPARKVIDEO:
			case ASSETS.ZIP:
			case MC.COMP:
			case MC.LAYUP:
			case MC.CLIP:
			case MC.SKETCH:
			case MC.DRAW:
			case MC.FIX:
			case MC.MIX:
			case MC.CLS:
				return GENERIC_ASSET_ICONS[type];

			default: {
				const defaultIconWithType = theme ? DEFAULT_ASSET_ICONS[`${type}_${theme}`] : DEFAULT_ASSET_ICONS[type];
				return (
					defaultIconWithType ||
					(theme && theme === 'dark' ? SEARCH_ICONS.UNKNOWN_ICON_DARK : SEARCH_ICONS.UNKNOWN_ICON)
				);
			}
		}
	}

	/**
	 * Get extension if any from filename
	 * @param {String} assetName
	 */
	static getExtensionFromFileName(assetName: string): string {
		let extensionFromFileName = '';

		if (GlobalsUtils.isString(assetName) && assetName.length > 0) {
			const extPosition = assetName.lastIndexOf('.');

			if (extPosition !== -1) {
				extensionFromFileName = assetName.slice(assetName.lastIndexOf('.') + 1);
			}
		}

		return extensionFromFileName;
	}

	/**
	 * Get file ext
	 * @param {String} mimeType
	 * @param {String} assetName
	 */
	static getFileExtensionFromMimeType(mimeType: string, assetName: string): { ext?: string; type?: string } {
		let returnedFileExtension = filesMap.unknown;

		if (GlobalsUtils.isString(mimeType) && mimeType.length > 0) {
			const dcxIndex = mimeType.lastIndexOf('dcxucf');
			let type = dcxIndex === -1 ? filesMap[mimeType] : filesMap[mimeType.substring(0, dcxIndex + 3)];

			// In case the file type is not included in `filesMap`
			if (!type) {
				getLogger().warn('Search Utils', 'Missing mimetype mapping', {
					mimetype: mimeType,
					name: assetName,
				});
				type = {
					ext: SearchUtils.getExtensionFromFileName(assetName) || 'app.type.unknown',
					type: 'app.type.unknown',
				};
			}

			returnedFileExtension = type;
		}

		return returnedFileExtension;
	}

	/**
	 * Return a default thumbnail.
	 * @param {String} theme
	 * @param {String} mimeType
	 * @param {String} assetName
	 */
	static getDefaultThumbnailURI(
		theme: string,
		mimeType?: string,
		assetName?: string,
		assetCategory?: string,
	): string {
		let thumbnailURI = theme === 'dark' ? DEFAULT_ASSET_ICONS.generic_dark : DEFAULT_ASSET_ICONS.generic;

		if (mimeType && GlobalsUtils.isString(mimeType) && assetName && GlobalsUtils.isString(assetName)) {
			const fileExtension = SearchUtils.getFileExtensionFromMimeType(mimeType, assetName);

			if (GlobalsUtils.isString(fileExtension.ext) && fileExtension.ext !== 'app.type.unknown') {
				let newFileExtension = fileExtension.ext.replace(/-|_| /g, '').toLowerCase();

				if (newFileExtension === 'folder' && assetCategory === 'space') {
					newFileExtension = 'space';
				}

				// Add dark theme for folder and sharedfolder icons
				if (theme === 'dark' && ['folder', 'sharedfolder'].indexOf(newFileExtension) > -1) {
					newFileExtension = `${newFileExtension}_${theme}`;
				}

				if (
					this.isSparkRebrandingEnabled() &&
					['sparkdefault', 'sparkpost', 'sparkvideo', 'sparkpage'].indexOf(newFileExtension) > -1
				) {
					const appMetadata: AppResultItem = (
						WebStorage.sessionStorage.getItem('searchAppMetadata') as Record<string, any>
					)?.apps?.FFC_ccexpress;

					if (appMetadata) {
						thumbnailURI = SearchUtils.getAppIconUrl(appMetadata.iconUrls);
					}
				} else {
					const fileExtensionWithTheme = theme === 'dark' ? `${newFileExtension}_${theme}` : newFileExtension;
					const defaultIcon =
						DEFAULT_ASSET_ICONS[fileExtensionWithTheme] ||
						DEFAULT_ASSET_ICONS[fileExtensionWithTheme?.toLowerCase()];
					thumbnailURI = defaultIcon ? defaultIcon : thumbnailURI;
				}
			}
		}

		return thumbnailURI;
	}

	/**
	 * Return true if it's an unknown icon thumbnail.
	 * @param {Object} url
	 */
	static isUnknownThumbnailURI(url: string): boolean {
		return url === DEFAULT_ASSET_ICONS.generic_dark || url === DEFAULT_ASSET_ICONS.generic;
	}

	/**
	 * Returns unknown icon thumbnail for theme
	 * @param {String} theme
	 */
	static getUnknownThumbnailURI(theme: string): string {
		return theme === 'dark' ? DEFAULT_ASSET_ICONS.generic_dark : DEFAULT_ASSET_ICONS.generic;
	}

	/**
	 * [hasNoResultsChecks checks to see if no results are found for a query]
	 * @param  {[Object]} result
	 * @returns {[boolean]}
	 */
	static hasNoResultsChecks(result: SearchCategoryResult): boolean {
		return !(
			result &&
			result.searchResults &&
			Array.isArray(result.searchResults.resultItems) &&
			result.searchResults.resultItems[0]
		);
	}

	/**
	 * [hasNoResults determines if we have empty search results]
	 * @param  {[array]} searchResults
	 * @returns {[boolean]}
	 */
	static hasNoResults(searchResponse: PublicSearchResponse) {
		return searchResponse?.data?.every((result) => this.hasNoResultsChecks(result)) ?? true;
	}

	/**
	 * getSearchCategoryData returns an array of result objects matching the specified category
	 * @param  {array} searchResults
	 * @param  {string} category
	 * @returns {array}
	 */
	static getSearchCategoryData(
		searchCategoryResults: Array<SearchCategoryResult>,
		category: string,
	): SearchCategoryResult | null | undefined {
		// Filter to the search category we're looking for.
		const filteredSearchCategories = searchCategoryResults?.filter(
			(result) => result.category?.toLowerCase() === category?.toLowerCase(),
		);

		// if matching results were found, return the first match (there should only be one)
		if (filteredSearchCategories && filteredSearchCategories.length) {
			const categoryData = filteredSearchCategories[0];
			categoryData.metadata.page =
				(categoryData.metadata.limit > 0 ? categoryData.metadata.start / categoryData.metadata.limit : 0) + 1;
			return categoryData;
		}
	}

	/**
	 * [getCurrentQuery gets the current query from url]
	 * @returns {[array]}
	 */
	static getCurrentQuery(): string {
		const hasQuery = location.search.indexOf('?query') > -1;

		if (hasQuery) {
			return decodeURIComponent(location.search.split('&')[0].replace('?query=', ''));
		}

		return '';
	}

	/**
	 * Detect key press
	 * @param {any} event
	 * @param {any} project
	 * @param {bool} stopPropagation Stop propagation of a child event to parent
	 * @memberof StartProject
	 */
	static onKeyDown(event: React.KeyboardEvent<HTMLElement>, stopPropagation?: boolean): void {
		if (event.key === ' ' || event.key === 'Enter' || event.which === 13 || event.which === 32) {
			if (stopPropagation) {
				event.stopPropagation();
			}

			event.currentTarget.click();
		}
	}

	static onKeyDownApp(event: React.KeyboardEvent<HTMLButtonElement>): void {
		if (event.key === ' ' || event.key === 'Enter' || event.which === 13 || event.which === 32) {
			try {
				// $FlowFixMe // FIXME: I'm not sure why Flow complains about this.
				// TODO: TS Check
				// @ts-ignore
				event.currentTarget.childNodes[0].children[0].click();
			} catch (e) {
				getLogger().error(
					'Search Util - onKeyDownApp',
					'Click handle not found for the event or the child node does not exists',
					e.toString(),
				);
			}
		}
	}

	/**
	 * [getCurrentURL gets the current document url]
	 * @returns {string}
	 */
	static getCurrentURL(): string {
		let currentURL = '';

		if (window && window.location && window.location.href) {
			currentURL = window.location.href;
		}

		return currentURL;
	}

	/**
	 * [listAllSearchDropdownItems gets the list of search dropdown items]
	 * @param {filteredSearchDropdownItems} filteredSearchDropdownItems
	 * @returns {Array<string>}
	 */
	static listAllSearchDropdownItems(filteredSearchDropdownItems: filteredSearchDropdownItems): Array<string> {
		let allDropdownItems = [];
		const orderOfDropDownItems = [
			'searchRecommendations',
			'leoOnboardingSummaryItems',
			'searchApps',
			'intentShortcuts',
			'searchHistory',
			'searchTrending',
			'searchAutoComplete',
			'searchRecommendationQuickLinks',
			'files',
		];
		if (filteredSearchDropdownItems && typeof filteredSearchDropdownItems === 'object') {
			orderOfDropDownItems.forEach((key) => {
				if (
					filteredSearchDropdownItems[key] &&
					Array.isArray(filteredSearchDropdownItems[key]) &&
					filteredSearchDropdownItems[key].length > 0
				) {
					allDropdownItems = allDropdownItems.concat(filteredSearchDropdownItems[key]);
				}
			});
		}

		return allDropdownItems;
	}

	/**
	 * [shouldShowSearchDropdownItems decides visibility of search dropdown]
	 * @param {filteredSearchDropdownItems} filteredSearchDropdownItems
	 * @returns {Array<string>}
	 */
	static shouldShowSearchDropdownItems(
		searchQuery: string,
		filteredSearchDropdownItems: filteredSearchDropdownItems,
	): boolean {
		const orderOfDropDownItems = [
			'searchRecommendations',
			'searchApps',
			'intentShortcuts',
			'searchHistory',
			'searchTrending',
			'searchAutoComplete',
		];
		if (SearchUtils.isHostFilesPanelActive()) {
			orderOfDropDownItems.push('files', 'recentFiles');
		} else {
			orderOfDropDownItems.push('leoOnboardingSummaryItems');
		}
		if (filteredSearchDropdownItems && typeof filteredSearchDropdownItems === 'object') {
			const showDropDown = orderOfDropDownItems.reduce((show, item) => {
				if (
					!searchQuery &&
					(item === 'searchRecommendations' ||
						item === 'searchHistory' ||
						item === 'recentFiles' ||
						item === 'leoOnboardingSummaryItems')
				) {
					return show || (filteredSearchDropdownItems[item]?.length ?? 0);
				}
				if (
					searchQuery &&
					item !== 'searchRecommendations' &&
					item !== 'leoOnboardingSummaryItems' &&
					item !== 'recentFiles'
				) {
					return show || (filteredSearchDropdownItems[item]?.length ?? 0);
				}
				return show;
			}, false);
			return Boolean(showDropDown);
		}

		return false;
	}

	/**
	 * [getSearchAppMetadataFromSessionStorage gets apps metadata from session storage]
	 * @param {HostContext} searchHostContext
	 * @returns {Promise<SearchAppMetadata>}
	 */
	static async getSearchAppMetadataFromSessionStorage(
		searchHostContext: HostContext,
		forceFetchFromServer = false,
	): Promise<SearchAppMetadata> {
		const returnData: SearchAppMetadata = {
			apps: {},
			locale: '',
			timestamp: '',
		};
		const searchAppMetadataFromSessionStorage: any = WebStorage.sessionStorage.getItem('searchAppMetadata');

		try {
			if (
				!forceFetchFromServer &&
				typeof searchAppMetadataFromSessionStorage === 'object' &&
				searchAppMetadataFromSessionStorage !== null &&
				searchAppMetadataFromSessionStorage.locale === searchHostContext.locale
			) {
				if (searchAppMetadataFromSessionStorage.apps) {
					returnData.apps = searchAppMetadataFromSessionStorage.apps;
				}

				returnData.locale = searchAppMetadataFromSessionStorage.locale;
				// return data from session storage if available
			} else {
				const searchService = new SearchService(searchHostContext);
				// fetching the app metaData from the search server
				const res: any = await searchService.fetchAppMetadata(searchHostContext.locale);

				//  let's fetch the data and store it in session storage
				if (res && Object.keys(res.apps).length > 0) {
					returnData.apps = res.apps;
					returnData.locale = searchHostContext.locale;
					// write the object to storage
					SearchUtils.setSearchAppMetadataToSessionStorage(returnData.apps, returnData.locale);
				}
			}
		} catch (err) {
			// network call failed, try fetching from session storage, report error
			getLogger().error('Search Utils', 'Unable to fetch app metaData from server', err.toString());
			getLogger().log('Search Utils', 'Fetching app metaData from session storage');

			if (
				typeof searchAppMetadataFromSessionStorage === 'object' &&
				searchAppMetadataFromSessionStorage !== null &&
				searchAppMetadataFromSessionStorage.locale === searchHostContext.locale
			) {
				if (searchAppMetadataFromSessionStorage.apps) {
					returnData.apps = searchAppMetadataFromSessionStorage.apps;
				}

				returnData.locale = searchAppMetadataFromSessionStorage.locale;
			} else {
				getLogger().log('Search Utils', 'Invalid app metaData in session storage returning default data');
			} // Else, return the default data
		}

		return returnData;
	}

	/**
	 * [setSearchAppMetadataToSessionStorage sets the apps metadata to session storage]
	 * @param {{[string]: AppResultItem}} data
	 * @param {string} locale
	 */
	static setSearchAppMetadataToSessionStorage(data: Record<string, AppResultItem>, locale: string): void {
		const searchAppMetadata: SearchAppMetadata = {
			apps: {},
			locale: '',
			timestamp: new Date().toISOString(),
		};
		searchAppMetadata.apps = data;
		searchAppMetadata.locale = locale;
		WebStorage.sessionStorage.setItem('searchAppMetadata', searchAppMetadata);
	}

	static getHistoryDataFromSessionStorage() {
		let returnData = [];
		const userId = SearchUtils.getUserIDForStorage();
		const data = WebStorage.sessionStorage.getItem('searchHistory');
		if (!SearchHostContext.isAuthenticated() && !SearchHostContext.getContext()?.canStoreHistory?.()) {
			return [];
		}

		if (data) {
			returnData = data[userId];

			if (!returnData || !Array.isArray(returnData) || !(returnData.length > 0)) {
				returnData = [];
			}
		}

		return returnData;
	}

	static setHistoryDataToSessionStorage(data: Array<string>) {
		const userId = SearchUtils.getUserIDForStorage();
		let historyDataWithUserInfo: any = WebStorage.sessionStorage.getItem('searchHistory');

		if (!SearchHostContext.isAuthenticated() && !SearchHostContext.getContext()?.canStoreHistory?.()) {
			return;
		}

		if (typeof historyDataWithUserInfo === 'object' && historyDataWithUserInfo !== null) {
			historyDataWithUserInfo[userId] = data;
		} else {
			historyDataWithUserInfo = {};
			historyDataWithUserInfo[userId] = data;
		}

		WebStorage.sessionStorage.setItem('searchHistory', historyDataWithUserInfo);

		// Clean up old search history data in local storage
		if (WebStorage.localStorage.getItem('searchHistory')) {
			WebStorage.localStorage.removeItem('searchHistory');
		}
	}

	static setSearchResultsCountToSessionStorage(category: string, data: Array<Record<string, any>>) {
		// add the count for this category to the searchResultsCountObject
		if (category && data && data.length > 0) {
			// get the searchResultsCountObject from storage
			let searchResultsCountObject: any = WebStorage.sessionStorage.getItem('searchResultsCountData');

			// no object found, let's create one
			if (!searchResultsCountObject) {
				searchResultsCountObject = {};
			}

			searchResultsCountObject[category] = data.length;
			// write the updated object to storage
			WebStorage.sessionStorage.setItem('searchResultsCountData', searchResultsCountObject);
		}
	}

	/**
	 * For an array of categories, get the number of results total for the categories combined.
	 * @param {Array<string>} categories
	 * @param {Object} countsMap
	 */
	static getSearchResultsForCategories(categories: Array<string>, countsMap: Record<string, any>) {
		let numResults = 0;

		for (const category of categories) {
			if (countsMap[category]) {
				numResults += countsMap[category];
			}
		}

		return numResults;
	}

	/*
	 * Adds the Search Query to Search History in local storage
	 */
	static setSearchHistoryWithSearchQuery(searchHistoryItems: Array<string>, item: string) {
		// This controls the maximum allowed search history items to be stored
		const searchHistoryStorageCount = 50;
		let updatedSearchHistoryItems: string[] = [];

		/* istanbul ignore next */
		if (typeof item === 'string' && item !== '') {
			const sanitizeInput = item.toLocaleLowerCase();

			if (searchHistoryItems && Array.isArray(searchHistoryItems) && searchHistoryItems.length > 0) {
				const searchHistoryItemsLowerCase = searchHistoryItems.map((item) => item.toLocaleLowerCase());
				const sanitizeInputIndex = searchHistoryItemsLowerCase.indexOf(sanitizeInput.toLocaleLowerCase());

				// Item doesn't exist in the array, hence we add it to the beginning of the array
				if (sanitizeInputIndex === -1) {
					searchHistoryItems.unshift(sanitizeInput);
				} else {
					// Item exists in the array, hence we move it to the beginning of the array with the latest query
					searchHistoryItems.splice(sanitizeInputIndex, 1);
					searchHistoryItems.unshift(sanitizeInput);
				}

				// Removes the last item in the array if the array length is greater than searchHistoryStorageCount
				if (searchHistoryItems.length > searchHistoryStorageCount) {
					const searchHistoryItemsDifferenceCount = searchHistoryItems.length - searchHistoryStorageCount;

					for (let i = 0; i < searchHistoryItemsDifferenceCount; i++) {
						searchHistoryItems.pop();
					}
				}

				// Updates the store with the latest history array
				updatedSearchHistoryItems = searchHistoryItems;
				SearchUtils.setHistoryDataToSessionStorage(searchHistoryItems);
			} else {
				updatedSearchHistoryItems = [sanitizeInput];
				SearchUtils.setHistoryDataToSessionStorage([sanitizeInput]);
			}

			return updatedSearchHistoryItems;
		}
	}

	/*
	 * Removes the duplicate search auto suggestion comparing search history and search trending
	 */
	static removeSearchAutoCompleteDuplicates(
		searchAutoCompleteItems: Array<any>,
		searchHistoryItems: Array<string>,
		searchTrendingItems: Array<string>,
	): Array<{ name: string; source: string }> {
		const totalSearchDropdownItemsCount = 6;
		let filteredSearchAutoCompleteItems: Array<{ name: string; source: string }> = [];
		let searchTrendingItemsLC: Array<string> = [];
		let searchHistoryItemsLC: Array<string> = [];
		if (searchAutoCompleteItems && Array.isArray(searchAutoCompleteItems) && searchAutoCompleteItems.length > 0) {
			let searchAutoCompleteItemsCount =
				totalSearchDropdownItemsCount - searchHistoryItems.length - searchTrendingItems.length;
			searchAutoCompleteItemsCount = searchAutoCompleteItemsCount > 0 ? searchAutoCompleteItemsCount : 0;
			if (Array.isArray(searchHistoryItems) && searchHistoryItems.length > 0) {
				searchHistoryItemsLC = searchHistoryItems.map((searchHistoryItem) =>
					searchHistoryItem.toLocaleLowerCase(),
				);
			}
			if (Array.isArray(searchTrendingItems) && searchTrendingItems.length > 0) {
				searchTrendingItemsLC = searchTrendingItems.map((searchTrendingItem) =>
					searchTrendingItem.toLocaleLowerCase(),
				);
			}
			const searchAutoCompleteItemsLC = searchAutoCompleteItems.map((searchAutoCompleteItem) => ({
				name: searchAutoCompleteItem?.name?.toLocaleLowerCase(),
				source: searchAutoCompleteItem?.source,
			}));
			filteredSearchAutoCompleteItems = searchAutoCompleteItemsLC.filter(
				(searchAutoCompleteItem) =>
					!searchHistoryItemsLC.includes(searchAutoCompleteItem.name) &&
					!searchTrendingItemsLC.includes(searchAutoCompleteItem.name),
			);
			// Reduce search auto suggestion list based on search history and search trending to display a maximum of totalSearchDropdownItemsCount items
			filteredSearchAutoCompleteItems = filteredSearchAutoCompleteItems.splice(0, searchAutoCompleteItemsCount);
		} else {
			filteredSearchAutoCompleteItems = searchAutoCompleteItems;
		}

		return filteredSearchAutoCompleteItems;
	}

	/**
	 * Process search history search server data.
	 */
	static processSearchServerSearchHistoryItems(searchServerSearchHistoryItems: searchServerSearchHistoryItems) {
		let searchHistoryItems = [];

		if (
			searchServerSearchHistoryItems &&
			searchServerSearchHistoryItems.searches &&
			searchServerSearchHistoryItems.searches.length > 0
		) {
			const searchServerSearchHistoryItemsSearches = searchServerSearchHistoryItems.searches;

			if (
				searchServerSearchHistoryItemsSearches &&
				Array.isArray(searchServerSearchHistoryItemsSearches) &&
				searchServerSearchHistoryItemsSearches.length > 0
			) {
				searchServerSearchHistoryItemsSearches.forEach((searchServerSearchHistoryItem: any) => {
					if (
						searchServerSearchHistoryItem &&
						typeof searchServerSearchHistoryItem.query === 'string' &&
						searchServerSearchHistoryItem.query.length > 0
					) {
						let searchHistoryQueryItem = searchServerSearchHistoryItem.query;
						searchHistoryQueryItem = searchHistoryQueryItem.toLocaleLowerCase();
						searchHistoryItems = searchHistoryItems.concat(searchHistoryQueryItem);
					}
				});
			} else {
				searchHistoryItems = SearchUtils.getHistoryDataFromSessionStorage();
			}
		}

		return searchHistoryItems;
	}

	/**
	 * Process search auto suggestion search server data.
	 */
	static processSearchServerSearchAutoCompleteItems(suggestions: Record<string, any> = {}): Array<any> {
		const searchAutoCompleteItems = suggestions?.suggested_completions ?? [];
		SearchUtils.searchAutoCompleteData = searchAutoCompleteItems;
		return searchAutoCompleteItems.filter((item: any) => item && item.name);
	}

	/**
	 * Sends a CustomEvent to inform listeners of a click on a Top Results source title
	 * @param {string} source name of the source
	 */
	static sendEventForTopResultsSourceClick(source: string) {
		const event = new CustomEvent('TopResultsSourceClick', {
			detail: {
				source,
			},
		});
		window.dispatchEvent(event);
	}

	/* Get dynamic Your Work filter options from the results.
	 * @param {Array<searchData>} data
	 */
	static getFilterOptions(searchConfig: Record<string, any>): YourWorkFilters {
		const searchHostContext = SearchHostContext.getContext();
		const configDoesHaveFilterOptions = searchConfig.ui && searchConfig.ui.filterOptions;
		// date filters will always be same
		// For Date Modified, you can only choose one option as its a RadioGroup.
		const dateFilter: YourWorkFilterOption = {
			label: SEARCH_YOUR_WORK_FILTERS.dateModified.label,
			optionsToDisplay: new Set([
				SEARCH_YOUR_WORK_FILTERS.dateModified.all,
				SEARCH_YOUR_WORK_FILTERS.dateModified.today,
				SEARCH_YOUR_WORK_FILTERS.dateModified.yesterday,
				SEARCH_YOUR_WORK_FILTERS.dateModified.lastSevenDays,
				SEARCH_YOUR_WORK_FILTERS.dateModified.lastThirtyDays,
				SEARCH_YOUR_WORK_FILTERS.dateModified.lastNinetyDays,
				SEARCH_YOUR_WORK_FILTERS.dateModified.lastYear,
			]),
			selectedOption: SEARCH_YOUR_WORK_FILTERS.dateModified.all,
		};
		// corresponds to DocumentTypes in SearchManagerTypes
		const typeFilter: YourWorkFilterOption = {
			label: SEARCH_YOUR_WORK_FILTERS.type.label,
			optionsToDisplay: new Set([
				SEARCH_YOUR_WORK_FILTERS.type.photoshop,
				SEARCH_YOUR_WORK_FILTERS.type.illustrator,
				SEARCH_YOUR_WORK_FILTERS.type.xd,
				SEARCH_YOUR_WORK_FILTERS.type.lightroom,
				SEARCH_YOUR_WORK_FILTERS.type.images,
			]),
			selectedOptions: new Set<string>(),
		};
		// corresponds to SourceLocations in SearchManagerTypes
		const locationFilter: YourWorkFilterOption = {
			label: SEARCH_YOUR_WORK_FILTERS.location.label,
			optionsToDisplay: new Set([
				SEARCH_YOUR_WORK_FILTERS.location.cloudDocuments,
				SEARCH_YOUR_WORK_FILTERS.location.syncedFiles,
				SEARCH_YOUR_WORK_FILTERS.location.mobileCreations,
				SEARCH_YOUR_WORK_FILTERS.location.libraries,
			]),
			selectedOptions: new Set<string>(),
		};
		// The array of filtered categories to hand back. We have an array so we can control the ORDER of the filter categories.
		let filterOptions: YourWorkFilters = [dateFilter, typeFilter, locationFilter];

		if (configDoesHaveFilterOptions) {
			// If the configuration from the server has its own set of filters, we should use those instead of the default coded ones above. Server config can therefore control the order of the filters + what categories appear.
			// There's a little massaging of the JSON data we need to do to return the filterOptions in the correct format, so let's do that.
			const searchConfigFilterOptions = searchConfig.ui.filterOptions;
			filterOptions = [];

			for (const category of searchConfigFilterOptions) {
				let optionsToDisplay = category.optionsToDisplay;
				if (category.label !== SEARCH_FILTERS_CATEGORY_LABELS.dateModified) {
					optionsToDisplay = ['all', ...optionsToDisplay];
				}
				const categoryObject: YourWorkFilterOption = {
					label: category.label,
					optionsToDisplay: new Set(optionsToDisplay),
					...(category.label === SEARCH_FILTERS_CATEGORY_LABELS.dateModified
						? { selectedOption: 'all' }
						: { selectedOptions: new Set(['all']) }),
				};
				filterOptions.push(categoryObject);
			}
		}
		return filterOptions;
	}

	/**
	 * [getFilteredSearchHistoryItems returns the list of filtered Search History Items by Search Query]
	 * @returns <Array<string>>
	 */
	static getFilteredSearchHistoryItems(searchQuery: string, searchHistoryItems: Array<string>) {
		let list: Array<string> = [];

		if (
			typeof searchQuery === 'string' &&
			searchHistoryItems &&
			Array.isArray(searchHistoryItems) &&
			searchHistoryItems.length > 0
		) {
			if (searchQuery !== '') {
				// Filters Search History Items based on Search Query
				const lowerCaseSearchQuery = searchQuery.toLocaleLowerCase();
				// Match Search Query from the start
				const re = new RegExp(`${SearchUtils.escapeRegExSpecialCharacters(lowerCaseSearchQuery)}`, 'g');
				list = searchHistoryItems.filter((item) => item.match(re));

				if (SearchHostContext.hasFeature('temp_enable_search_auto_suggestion_server_uss')) {
					// Return only the top 2 search history items
					list = list.slice(0, 2);
				}
			} else {
				list = searchHistoryItems;
				// Return only the top 6 search history items
				list = list.slice(0, 6);
			}
		}

		return list;
	}

	/**
	 * [getFilteredSearchTrendingItems returns the list of filtered Search Trending Items by Search Query]
	 * @returns <Array<string>>
	 */
	static getFilteredSearchTrendingItems(
		searchQuery: string,
		searchTrendingItems: Array<string>,
		searchHistoryItems: Array<string>,
	) {
		let list: Array<string> = [];

		if (
			typeof searchQuery === 'string' &&
			searchTrendingItems &&
			Array.isArray(searchTrendingItems) &&
			searchTrendingItems.length > 0
		) {
			if (searchQuery !== '') {
				// Filters Search Trending Items based on Search Query
				const lowerCaseSearchQuery = searchQuery.toLocaleLowerCase();
				// Match Search Query from the start
				const re = new RegExp(`^${SearchUtils.escapeRegExSpecialCharacters(lowerCaseSearchQuery)}`, 'g');
				const unfilteredList = searchTrendingItems.filter((item) => item.match(re));
				if (searchHistoryItems && Array.isArray(searchHistoryItems) && searchHistoryItems.length > 0) {
					const searchTrendingItemsLC = unfilteredList.map((searchTrendingItem) =>
						searchTrendingItem.toLocaleLowerCase(),
					);
					const searchHistoryItemsLC = searchHistoryItems.map((searchHistoryItem) =>
						searchHistoryItem.toLocaleLowerCase(),
					);
					list = searchTrendingItemsLC.filter(
						(searchTrendingItemLC) => !searchHistoryItemsLC.includes(searchTrendingItemLC),
					);
				} else {
					list = unfilteredList;
				}
				list = list.slice(0, 2);
			} else {
				list = searchTrendingItems;
				// Return only the top 6 search trending items
				list = list.slice(0, 5);
			}
		}

		return list;
	}

	/**
	 * It takes in an array of recommendations and an object of app metadata, and returns an array of
	 * recommendation item names
	 * @param {Record<string, any>[]} recommendations - Record<string, any>[] - This is the list of
	 * recommendations that you get from the search API.
	 * @param {AppMetadata} searchAppMetadataList - AppMetadata
	 * @returns An array of strings.
	 */
	static getFilteredSearchDropDownRecommendationItems(
		recommendations: Record<string, any>[],
		searchAppMetadataList: AppMetadata,
	): Array<string> {
		const recommendationItemNames = [];

		recommendations.forEach((recommendation) => {
			if (recommendation.recommendationType.toLowerCase() !== 'quick links') {
				let { name } = recommendation;
				const { type, ffcId } = recommendation;
				if (type === 'apps') {
					const appMetadata: AppResultItem = searchAppMetadataList?.apps
						? searchAppMetadataList.apps[ffcId]
						: {};
					name = appMetadata?.name;
				}
				recommendationItemNames.push(name);
			}
		});
		return recommendationItemNames;
	}
	/**
	 * [determineWhichKnowledgetoShow returns which knowledge cards to show based on query]
	 * @returns <string>
	 */
	static determineWhichKnowledgeCardApptoShow(query: string) {
		const lowerCaseQuery = query.toLowerCase();
		const isPSQuery = TOP_PS_QUERIES.indexOf(lowerCaseQuery) > -1;
		const isCCQuery = TOP_CC_QUERIES.indexOf(lowerCaseQuery) > -1;
		let displayApp = '';

		if (isPSQuery) {
			displayApp = 'ps';
		} else if (isCCQuery) {
			displayApp = 'cc';
		}

		return displayApp;
	}

	/**
	 * [getSearchQueryItemSplitArray returns the searchQuery match in bold in searchItem]
	 * @returns <Array<string>>
	 */
	static getSearchQueryItemSplitArray(searchItem: string, searchQuery: string) {
		let result: string[] = [];

		if (
			searchItem &&
			typeof searchItem === 'string' &&
			searchQuery &&
			typeof searchQuery === 'string' &&
			searchQuery !== ''
		) {
			const re = new RegExp(`(${SearchUtils.escapeRegExSpecialCharacters(searchQuery)})`, 'g');
			result = searchItem.split(re);
		} else {
			result = [searchItem];
		}

		return result;
	}

	/**
	 * Fetches the button preference from session storage.
	 */
	static getViewButtonInfoFromSessionStorage(isFirstLoad: boolean) {
		// This defines the default view button behaviour
		const viewButtonInfo = {
			view: 'gridView',
			showFilters: true,
		};

		if (WebStorage.sessionStorage.getItem('searchResultsPageYourWorkButtonInfo')) {
			const buttonInfo: any = WebStorage.sessionStorage.getItem('searchResultsPageYourWorkButtonInfo');

			if (buttonInfo.view) {
				viewButtonInfo.view = buttonInfo.view;
			}

			if (typeof buttonInfo.showFilters === 'boolean') {
				viewButtonInfo.showFilters = buttonInfo.showFilters;
			}
		}

		// If the User loads the page on the XS breakpoint, we hide the filters by default
		if (
			Utils.currentBreakpoint === 'XS' ||
			Utils.currentBreakpoint === 'SS' ||
			(Utils.currentBreakpoint === 'S' && isFirstLoad)
		) {
			viewButtonInfo.showFilters = false;
		} else {
			viewButtonInfo.showFilters = true;
		}
		return viewButtonInfo;
	}

	/**
	 * Stores the button preference to session storage.
	 */
	static setViewButtonInfoToSessionStorage(button: string) {
		const viewButtonInfo = SearchUtils.getViewButtonInfoFromSessionStorage(false);

		switch (button) {
			case 'listView':
				viewButtonInfo.view = 'listView';
				break;

			case 'gridView':
				viewButtonInfo.view = 'gridView';
				break;

			case 'showFilters':
				viewButtonInfo.showFilters = true;
				break;

			case 'hideFilters':
				viewButtonInfo.showFilters = false;
				break;

			default:
				break;
		}

		WebStorage.sessionStorage.setItem('searchResultsPageYourWorkButtonInfo', viewButtonInfo);
	}

	/**
	 * Extracts the path of the synced file from the raw data; returns empty string if the file is not synced file,
	 * or if there is no path
	 * @param  {[Object]} data
	 * @returns {[string]}
	 */
	static extractTopBottomPath(data: TableResultItem) {
		let result = '';

		if (data) {
			const topFolder = data.localizedAssetCategory;
			let separator = '';
			let bottomFolder = '';

			if (
				data.resultItem &&
				data.resultItem.attributes &&
				data.resultItem.attributes.assetCategory === APP_TYPES.CREATIVE_CLOUD
			) {
				const rawPath = data.resultItem.attributes.path;

				if (rawPath) {
					const lastIndexOfFolderSeparator = rawPath.lastIndexOf('/');

					if (lastIndexOfFolderSeparator !== 0) {
						// NOTE: rawPath is in the form /files/subfolder1/subfolder2,
						// thus lastIndexOfFolderSeparator === 6 means there are only
						// 2 folders (/files has 6 characters), so the ... should not be shown
						separator = lastIndexOfFolderSeparator === 6 ? ' > ' : ' > ... > ';
						bottomFolder = rawPath.substring(lastIndexOfFolderSeparator + 1);
					}

					result = topFolder.concat(separator, bottomFolder);
				}
			}
		}

		return result;
	}

	/**
	 * Util function to round out the RGB values.
	 */
	static roundDownRGB(rgb: RGBColor): RGBColor {
		const rgbCopy = JSON.parse(JSON.stringify(rgb));
		rgbCopy.r = Math.round(rgbCopy.r);
		rgbCopy.g = Math.round(rgbCopy.g);
		rgbCopy.b = Math.round(rgbCopy.b);
		return rgbCopy;
	}

	/**
	 * [getFirstSparkTemplateForBlendedResults] returns the first ccexpressTemplate
	 * with ussContext parameters added for use in blendedResults.
	 * Returns nothing if there is no ccexpressTemplate.
	 * @param {Object} searchResults
	 */
	static getFirstSparkTemplateForBlendedResults(searchResults: Array<SearchCategoryResult>) {
		let result = {};

		try {
			const searchCategoryData = this.getSearchCategoryData(searchResults, 'sparkTemplates');

			if (searchCategoryData) {
				const sparkTemplatesAttributes = searchCategoryData.searchResults;
				const firstSparkTemplate = JSON.parse(JSON.stringify(sparkTemplatesAttributes.resultItems[0]));
				firstSparkTemplate.attributes.ussContext = {
					scope: 'spark',
					group: 'sparkTemplates',
				};
				result = firstSparkTemplate;
			}
		} catch (e) {
			// Will reach here if there are no ccexpressTemplates
			getLogger().error(
				'Search Utils',
				'Error getting first ccexpress template for blended results (no ccexpress template)',
				e.toString(),
			);
		}

		return result;
	}

	/**
	 * [getBlendedResults] returns an object containing blended results with one ccexpress template inserted
	 * at the end if there are fewer than 6 top blended results and there is one ccexpress template.
	 * If searchResults does not contain blendedResults, return {}.
	 * @param  {Object} searchResults
	 * @param {number} numResults
	 * @returns {topResultsData}
	 */
	static getBlendedResults(searchResults: Array<SearchCategoryResult>): SearchResult {
		const result: Record<string, any> = {};

		try {
			const searchCategoryData = this.getSearchCategoryData(searchResults, 'blended_results');

			if (searchCategoryData) {
				const blendedResultsAttributes = JSON.parse(JSON.stringify(searchCategoryData.searchResults));
				result.totalResults = blendedResultsAttributes.totalResults;
				result.resultItems = blendedResultsAttributes.resultItems;

				if (result.totalResults < 6) {
					const firstSparkTemplate = SearchUtils.getFirstSparkTemplateForBlendedResults(searchResults);

					if (firstSparkTemplate && Object.keys(firstSparkTemplate).length !== 0) {
						result.totalResults = result.totalResults + 1;
						result.resultItems.push(firstSparkTemplate);
					}
				}
			}
		} catch (e) {
			getLogger().error('Search Utils', 'Error getting blended result', e.toString());
		}

		return result as SearchResult;
	}

	/**
	 * [getSourceForTopResult] returns the source of the results in topResults.
	 * Returns '' if there is no ussContext.
	 * @param  {ResultItem} result
	 * @returns {string}
	 */
	static getSourceForTopResult(result: ResultItem) {
		let source = '';

		try {
			if (result && result.attributes && result.attributes.ussContext) {
				source = result.attributes.ussContext.group;
			}
		} catch (e) {
			getLogger().error('Search Utils', 'Error getting source for top result', e.toString());
		}

		return source;
	}

	/**
	 * [shouldUseBlendedResults] determines whether or not we should show blended results based off of host information + feature flag + search configuration.
	 * @returns {bool}
	 */
	static shouldUseBlendedResults(searchConfig: Record<string, any>): boolean {
		const shouldUseBlendedResults = searchConfig?.ui?.features?.blendedResults;
		return shouldUseBlendedResults;
	}

	/**
	 * [getCategoryOrderInBlendedResults] returns the category order from USS results.
	 * Inserts 'apps' at the top, followed by USS results (Helps + Stocks),
	 * followed by 'ccexpressTemplates', followed by 'yourWork'.
	 * @param {Object} searchResults
	 */
	static getCategoryOrderInBlendedResults(searchResults: Array<SearchCategoryResult>): string[] {
		const categoryArray = [];
		searchResults.forEach((result) => {
			// TODO: check for blended results, hints in v3/v2 search call
			if (result.type && result.type !== 'source' && result.id === 'stock') {
				categoryArray.push(result.type);
			} else if (result.id && result.id !== 'stock') {
				// to skip id=stock and type=source case
				categoryArray.push(result.id);
			}
		});
		return [...new Set([...categoryArray, 'yourWork'])];
	}

	/**
	 * Move AX templates above stock in search results
	 * @param  - `searchHostContext`: The `HostContext` object that was passed to the `Search` component.
	 * @param  -  `categories`: categoryorder of items
	 * @returns - categories with AX templates moved above stock assets
	 */
	static transposeAXTemplatesOrder(categories: string[]): string[] {
		let categoryTransposed: string[] = [];
		categoryTransposed = categories.filter((categoryItem) => categoryItem !== 'sparkTemplates');
		const indexOfStock = Math.min(categoryTransposed.indexOf('stock'), categoryTransposed.indexOf('templates'));
		if (indexOfStock !== -1) {
			categoryTransposed.splice(indexOfStock, 0, 'sparkTemplates');
			return categoryTransposed;
		}
		return categories;
	}

	/**
	 * The function `getRenderCategoriesOrder` filters and returns categories from `ussCategoryArray`
	 * based on the order specified in `ussCategoryArray`.
	 * @param ussCategoryArray - The `ussCategoryArray` parameter is an array containing categories from server result.
	 * @param configCategories - An array of categories that define the categories that should be render.
	 * @returns The `getRenderCategoriesOrder` function returns an array of categories from
	 * `ussCategoryArray` that match the categories in `configCategories`. The returned array contains the
	 * categories in the order they appear in `ussCategoryArray`.
	 */
	static getRenderCategoriesOrder(ussCategoryArray: string[], configCategories: string[]) {
		try {
			const categoryArray = [];
			const lowercaseConfigCategories = configCategories.map((category) => category.toLowerCase());

			ussCategoryArray.forEach((category) => {
				const lowercaseCategory = category.toLowerCase();
				const index = lowercaseConfigCategories.indexOf(lowercaseCategory);
				if (index !== -1) {
					categoryArray.push(configCategories[index]);
				}
			});
			return categoryArray;
		} catch (e) {
			getLogger().error('Search Utils', 'Error getting render categories order', e.toString());
			return configCategories;
		}
	}
	/**
	 * [getSelectedCategoryOrderInBlendedResults] returns the category order from USS results.
	 * Inserts 'apps' at the top, followed by USS results (Helps + Stocks),
	 * followed by 'ccexpressTemplates', followed by 'yourWork'.
	 * @param {Object} searchResults
	 * @param {[string]} categoriesToConsider
	 */
	static getSelectedCategoryOrderInBlendedResults(
		searchResults: Array<SearchCategoryResult>,
		categoriesToConsider: Array<string>,
	): string[] {
		const categoryArray: string[] = [];
		searchResults.forEach((result) => {
			const category = result.type || result.category;
			if (
				result.type &&
				categoriesToConsider.map((category) => category.toLowerCase()).includes(category?.toLowerCase())
			) {
				const resultType = result.type[0].toLowerCase() + result.type.slice(1, result.type.length);
				categoryArray.push(resultType);
			}
		});
		return categoryArray;
	}

	/**
	 * Determine if the theme is dark mode.
	 * @param {string} theme
	 * @returns {Boolean}
	 */
	static isDarkMode(theme: string): boolean {
		return theme === UI_THEMES.DARK || theme === UI_THEMES.DARKEST;
	}

	/**
	 * Determine if the host surface is CCX
	 * or host client is CCAssets inorder to apply Border box to carousel arrows.
	 *   'CCInApp' is the standard name for CCX apps
	 *  'CCAssets' is CCAssets client name
	 * @param {string} theme
	 * @returns {Boolean}
	 */
	static applyBorderBox() {
		const searchHostContext: HostContext = SearchHostContext.getContext();
		return searchHostContext.hostInfo.surface === 'CCInApp';
	}

	/**
	 * Get the localized file type
	 * @param {string} mimeType
	 */
	static getFileType(item: ResultItem, props: Record<string, any>): string {
		const searchHostContext: HostContext = SearchHostContext.getContext();
		const locale = searchHostContext.locale;
		const mimeType: string = item.attributes.mimeType || '';
		let ext = '';

		if (mimeType) {
			const mimeTypeData: TranslationData = FileTypeService.fileTypeDataObject?.translations[
				mimeType
			] as TranslationData;

			if (mimeTypeData) {
				// start by setting to the nor
				ext = mimeTypeData.ext;

				// We utilize the specializations data if it exists.
				// for this function we ONLY care about ext data
				if (mimeTypeData.specializations && mimeTypeData.specializations.ext) {
					// grab ext data and it's keys.
					const specializationExtData = mimeTypeData.specializations.ext;
					const specializationExtDataKeys = Object.keys(specializationExtData);

					// loop through the keys we are specializing on, in our sample data
					// above we only have a single item, assetType
					for (const key of specializationExtDataKeys) {
						// from our result item let's see if a key named property exists
						// and if so, objProp will be the value of that. in our first
						// sample item above, objProp will be designSpec, and for the
						// second it will be prototype.
						const objProp: any = this.getNestedProperty(item, key);

						if (objProp) {
							// this will get the most leafy keys in our translation data
							// in our sample above this will be [designSpec, prototype]
							const matchingPropKeys: Array<string> = Object.keys(specializationExtData[key]);

							for (const innerKey of matchingPropKeys) {
								// if the key matches the value of the object property, we got it.
								// I use a RegEx match so that this can be used for things like file extension
								// comparisons as well.
								if (objProp.match(innerKey)) {
									ext = specializationExtData[key][innerKey];
								}
							}
						}
					}
				}
			}
		}

		// if the mimeType is missing, try and get the ext
		if (!ext && item.attributes.name.length > 0) {
			const parsedArray = item.attributes.name.split('.');

			// length must have two parts to have an extention
			if (parsedArray.length > 1) {
				ext = parsedArray[parsedArray.length - 1];
			}
		}

		if (!ext) {
			if (item.assetType === 'document') {
				ext = props.intl.formatMessage({
					id: 'app.search.assetCategory.document',
				});
			} else if (item.assetType === 'libraryElement') {
				ext = props.intl.formatMessage({
					id: 'app.search.libraries_element_asset.title',
				});
			} else {
				if (FileTypeService.fileTypeDataObject?.translations?.unknown) {
					ext = (FileTypeService.fileTypeDataObject.translations.unknown as TranslationData).ext;
				}

				getLogger().warn('Search Utils', 'getFileType: Missing mimetype mapping', {
					mimetype: mimeType,
					name: item.attributes.name,
					locale,
				});
			}
		}

		/**
		 * This is hardcoded till we need projects everywhere
		 */
		const spacesRebranded = SearchUtils.isProjectRebrandingEnabled();
		if (item.assetType === 'space' && spacesRebranded) {
			ext = props.intl.formatMessage({
				id: 'app.search.yourwork.project.title',
			});
		}
		return ext;
	}

	/**
	 * given a string of the form, x.y.z this function will dig into
	 * an object and return the property at that position, for example
	 * in the object below the result getNestedProperty(foo, 'x.y.z')
	 * would yield, "aValue". if the nested property does not
	 * exist the function will return undefined
	 * const foo = {
	 * 		x: {
	 * 			y: {
	 * 				z: 'aValue
	 * 			}
	 * 		}
	 * }
	 * @param {Object} obj
	 * @param {string} path
	 */
	static getNestedProperty(obj: Record<string, any>, path: string): any {
		return path.split('.').reduce((obj, path) => obj[path], obj);
	}

	/**
	 * Sets the server calls timing information
	 * @param {string} name
	 * @param {string} type
	 * @param {number} duration
	 */
	static saveSearchTimingInSessionStorage(name: string, type: string, duration: number): void {
		if (typeof name === 'string' && typeof type === 'string' && duration > 0) {
			// get the searchResultsTimingData from storage
			let searchResultsTimingData: any = WebStorage.sessionStorage.getItem('searchResultsTimingData');

			// no object found, let's create one
			if (!searchResultsTimingData) {
				searchResultsTimingData = {
					maxTime: 0,
					data: {},
				};
			}

			if (searchResultsTimingData && searchResultsTimingData.data) {
				if (!searchResultsTimingData.data[name]) {
					searchResultsTimingData.data[name] = {
						maxTime: 0,
						data: {},
					};
				}

				searchResultsTimingData.data[name].data[type] = duration;
			}

			searchResultsTimingData = SearchUtils.updateMaxSearchTiming(searchResultsTimingData);
			// write the updated object to storage
			WebStorage.sessionStorage.setItem('searchResultsTimingData', searchResultsTimingData);
			getLogger().log('Search Utils', 'Updated server calls timing information', searchResultsTimingData);
		}
	}

	/**
	 * Resets the server calls timing information
	 */
	static resetSearchTimingInSessionStorage(): void {
		const searchResultsTimingData = {
			maxTime: 0,
			data: {},
		};
		// write the updated object to storage
		WebStorage.sessionStorage.setItem('searchResultsTimingData', searchResultsTimingData);
	}

	/**
	 * Updates the max timing data for saveSearchTimingInSessionStorage
	 */
	static updateMaxSearchTiming(searchResultsTimingData: Record<string, any>) {
		const modifiedSearchResultsTimingData = searchResultsTimingData;
		const itemMaxTimeData: number[] = [];

		// Find the max time taken for each name
		if (
			searchResultsTimingData &&
			searchResultsTimingData.data &&
			Object.keys(searchResultsTimingData.data).length > 0
		) {
			Object.keys(searchResultsTimingData.data).forEach((name) => {
				if (
					searchResultsTimingData.data[name].data &&
					Object.values(searchResultsTimingData.data[name].data).length > 0
				) {
					const maxTimeData: number[] = [];
					Object.values(searchResultsTimingData.data[name].data).forEach((duration) => {
						if (typeof duration === 'number') {
							maxTimeData.push(duration);
						}
					});
					modifiedSearchResultsTimingData.data[name].maxTime = Math.max(...maxTimeData);
					itemMaxTimeData.push(Math.max(...maxTimeData));
				}
			});
		}

		if (
			itemMaxTimeData.length > 0 &&
			searchResultsTimingData &&
			typeof searchResultsTimingData.maxTime === 'number'
		) {
			searchResultsTimingData.maxTime = Math.max(...itemMaxTimeData);
		}

		return modifiedSearchResultsTimingData;
	}

	/**
	 * Returns the appropriate icon size URL
	 * @returns {string}
	 */
	static getPluginsIconUrl(iconUrls: Array<PluginIcon>): string {
		const requiredSize = 96;
		let iconUrl = APPS_TO_CODES_MAPPING.default_offline;
		let width = 96;
		if (iconUrls && iconUrls.length > 0) {
			for (const eachIconUrl of iconUrls) {
				if (eachIconUrl.width < width) {
					iconUrl = eachIconUrl.href;
					width = eachIconUrl.width;
				}

				if (eachIconUrl.width === requiredSize) {
					iconUrl = eachIconUrl.href;
					break;
				}
			}
		}
		return iconUrl;
	}
	/**
	 * Returns the appropriate icon size URL
	 * @param  {[{number: string}]} iconUrls
	 * @returns {string}
	 */
	static getAppIconUrl(iconUrls: { [key: number]: string }): string {
		const requiredSize = 96;
		let iconUrl = APPS_TO_CODES_MAPPING.default_offline;

		if (iconUrls && Object.keys(iconUrls).length > 0) {
			if (iconUrls[requiredSize.toString()]) {
				iconUrl = iconUrls[requiredSize.toString()];
			} else {
				// List available icon sizes
				const availableSizes = Object.keys(iconUrls)
					.map((item) => parseInt(item))
					.filter((item) => !isNaN(item));

				// We pick the maximum available size
				if (Array.isArray(availableSizes) && availableSizes.length > 0) {
					const maxSize = Math.max(...availableSizes).toString();
					iconUrl = iconUrls[maxSize];
				}
			}
		}

		return iconUrl;
	}
	/**
	 * Returns the appropriate translation of app type
	 * @param  {[string]} appType
	 * @returns {string}
	 */
	static getAppTypeLocalizedKey(appType: string): string {
		let appTypeLocalized = '';
		const typeMapping = {
			Desktop: 'app.search.apps.type.desktop.app',
			'Mobile App': 'app.search.apps.type.mobile.app',
			Service: 'app.search.apps.type.web.app',
		};

		if (typeMapping[appType]) {
			appTypeLocalized = typeMapping[appType];
		}

		return appTypeLocalized;
	}

	/**
	 * Returns the appropriate translation of app type
	 * @param  {[string]} appType
	 * @returns {string}
	 */
	static getLocalizedAppType(appType: string): string {
		let appTypeLocalized = '';
		const typeMapping = {
			Desktop: 'app.search.apps.type.desktop.quickLink',
			'Mobile App': 'app.search.apps.type.mobile.quickLink',
			Service: 'app.search.apps.type.web.quickLink',
		};

		if (typeMapping[appType]) {
			appTypeLocalized = typeMapping[appType];
		}

		return appTypeLocalized;
	}

	/**
	 * Checks if the feature flag is in a test bucket (not control bucket)
	 *
	 * We match the featureFlag with each testBucket in testBuckets
	 * and return true if we find a match with any testBuckets
	 *
	 * @param  {[string, Array<string>]} featureFlag, testBuckets
	 * @returns {boolean}
	 */
	static isFloodgateFeatureInTest(featureFlag: string, searchHostContext: HostContext): boolean {
		let isFloodgateFeatureInTest = false;
		const floodgateService = new FloodgateService(searchHostContext);
		const floodgateFeaturesInTestObj = floodgateService.getFloodgateCampaignsAndFeatures().features;

		// Check if the provided feature flag is a string
		if (featureFlag && typeof featureFlag === 'string' && floodgateFeaturesInTestObj) {
			if (floodgateFeaturesInTestObj[featureFlag]) {
				isFloodgateFeatureInTest = true;
			}
		}

		return isFloodgateFeatureInTest;
	}

	/**
	 * Determines whether to use the aep endpoint for migration
	 * @returns {boolean} true if should use aep endpoint, false otherwise
	 */
	static isFloodgateMigrated(): boolean {
		const logger = getLogger();
		const searchHostContext: HostContext = SearchHostContext.getContext();

		if (!searchHostContext) {
			logger.log('SearchUtils - Migration Decision', 'No host context available, defaulting to false');
			return false;
		}

		if (searchHostContext.featureFlags && searchHostContext.featureFlags.includes('ccs_floodgate_migrated')) {
			logger.log('SearchUtils - Migration Decision', 'Using AEP Endpoint');
			return true;
		}

		logger.log('SearchUtils - Migration Decision', 'Using Sophia Endpoint');
		return false;
	}

	static isUserOptInForLeoResponse(): boolean {
		return !!(SearchHostContext.isAuthenticated() && UserPreferencesModel.getGenAIPreference());
	}
	static isLeoFloodgateFeatureInTest(featureFlag: string, searchHostContext: HostContext): boolean {
		return (
			SearchHostContext.isAuthenticated() &&
			SearchUtils.isUserOptInForLeoResponse() &&
			SearchUtils.isFloodgateFeatureInTest(featureFlag, searchHostContext)
		);
	}

	static isSparkRebrandingEnabled(): boolean {
		const searchConfig = SearchConfig.getSearchConfig();

		// check if Feature flag is set

		/**
		 * ccsearch_enable_spark_rebranding is client side flag for testing purpose
		 * sparkRebranding is a server side flag, on production environment this flag will be used to enable this feature.
		 */
		if (
			!SearchHostContext.hasFeature('ccsearch_enable_spark_rebranding') &&
			(!searchConfig ||
				!searchConfig.ui ||
				!searchConfig.ui.features ||
				!searchConfig.ui.features.sparkRebranding)
		) {
			return false;
		}

		return true;
	}
	static isAdobeExpressRebrandingEnabled(): boolean {
		const searchConfig = SearchConfig.getSearchConfig();

		// check if Feature flag is set

		/**
		 * ccsearch_enable_adobeexpress_rebranding is client side flag for testing purpose
		 * adobeExpressRebranding is a server side flag, on production environment this flag will be used to enable this feature.
		 */
		if (
			!SearchHostContext.hasFeature('ccsearch_enable_adobeexpress_rebranding') &&
			(!searchConfig ||
				!searchConfig.ui ||
				!searchConfig.ui.features ||
				!searchConfig.ui.features.adobeExpressRebranding)
		) {
			return false;
		}

		return true;
	}

	static getTranslationIdForSparkRebranding(translationId: string): string {
		if (!this.isAdobeExpressRebrandingEnabled()) {
			return translationId;
		}

		const sparkToCCExpressTranslationIds = {
			'app.search.ccexpress.title': 'app.search.adobeexpress.title',
			'app.search.ccexpress_templates.title': 'app.search.adobeexpress_templates.title',
			'app.search.see_all_ccexpress_template': 'app.search.see_all_adobeexpress_template',
			'app.search.yourwork.spillover.goto.ccexpress': 'app.search.yourwork.spillover.goto.adobeexpress',
		};
		return sparkToCCExpressTranslationIds[translationId] || translationId;
	}

	static getIDForSparkBaseURL(): string {
		return this.isSparkRebrandingEnabled() ? 'CCEXPRESS_PROJECT_BASE_URL' : 'SPARK_PROJECT_BASE_URL';
	}

	static setPlaceHolderIndexInLocalStorage(placeholderSelectedIndex): void {
		WebStorage.localStorage.setItem('placeholderSelectedIndex', placeholderSelectedIndex);
	}

	static getPlaceholderIndexInLocalStorage(): number {
		const lastSelectedIndex: any = WebStorage.localStorage.getItem('placeholderSelectedIndex');
		return lastSelectedIndex ? parseInt(lastSelectedIndex) : -1;
	}
	static async checkIfHostContextCanLaunchApp({
		hostContext,
		appMetadata,
		waitForWAMConnection = false,
	}: {
		hostContext: HostContext;
		appMetadata: Record<string, any>;
		waitForWAMConnection?: boolean;
	}): Promise<boolean> {
		try {
			let isAppLaunchable: Promise<boolean> | boolean = false;
			if (!SearchHostContext.isAuthenticated() || typeof hostContext.isAppLaunchable != 'function') {
				isAppLaunchable = false;
			} else if (typeof hostContext.isAppLaunchable === 'function') {
				const appId = appMetadata.sapCode || appMetadata.ffcId || appMetadata.id;
				if (appMetadata.nonCC) {
					isAppLaunchable = false;
				} else if (
					hostContext.hostInfo.surface === 'CCDesktop' &&
					hostContext.hostInfo.client === 'CCDesktop'
				) {
					isAppLaunchable = hostContext.isAppLaunchable(
						appId,
						appMetadata.type && appMetadata.type.toLowerCase(),
						{ waitForWAMConnection },
					);
				} else if (appMetadata.type === 'Service' && appMetadata.serviceLaunchUrl === undefined) {
					isAppLaunchable = false;
				} else {
					isAppLaunchable = await hostContext.isAppLaunchable(
						appId,
						appMetadata.type && appMetadata.type.toLowerCase(),
						{ waitForWAMConnection },
					);
				}
			}

			return Promise.resolve(isAppLaunchable);
		} catch (error) {
			return Promise.resolve(false);
		}
	}

	static executeAppLaunch({
		searchHostContext,
		appMetadata,
		searchAppMetadataList,
		appRenderType,
	}: {
		searchHostContext: HostContext;
		searchAppMetadataList: any;
		appMetadata: Record<string, any>;
		appRenderType?: string;
	}): void {
		try {
			const renderType = appRenderType || appMetadata.renderType;
			const appId = appMetadata.sapCode || appMetadata.ffcId || appMetadata.id;
			if (
				searchHostContext.hostInfo.surface === 'CCDesktop' &&
				searchHostContext.hostInfo.client === 'CCDesktop'
			) {
				searchHostContext.launchApp(appId, renderType, this.getCCDCgenParams(appId, renderType));
			} else {
				let launchUrl = searchAppMetadataList?.apps[appId]?.serviceLaunchUrl;
				switch (renderType) {
					case SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_SHORTCUT:
						launchUrl = appMetadata.type === 'Service' ? appMetadata.serviceLaunchUrl : appMetadata.url;
						break;
					case SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.PRE_SEARCH_RECOMMENDATION:
					case SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.APP_QUICK_LINK:
					default:
						launchUrl = searchAppMetadataList.apps[appId].serviceLaunchUrl;
				}
				const dataObj = {
					sapcodeAppId: appId,
					appLaunchLocation: renderType,
					cliendID: searchHostContext.hostInfo.client,
					applicationLaunchUrl: launchUrl,
				};
				launchUrl = this.addCgenParamsForWeb(dataObj);
				searchHostContext.launchApp(
					appId,
					renderType,
					appMetadata.type.toLowerCase(),
					appMetadata.type === 'Service' &&
						getLocalizedURL(launchUrl, normalize(searchHostContext.locale, 'ccsearch')),
				);
			}
		} catch (error) {
			// TODO : think of a default action
			getLogger().error('Search Utils', 'executeAppLaunch', error);
		}
	}

	/*
	 * Returns the appropriate app type for analytics
	 * @return {string}
	 */
	static getAppTypeForAnalytics(type: string): string {
		const typeMapping = {
			Desktop: 'desktop',
			'Mobile App': 'mobile',
			Service: 'web',
		};

		return typeMapping[type];
	}

	/**
	 * Check whether user has selected any filters.
	 * Get selected filters from UI display options
	 * @param filters -> The filters that the user has selected.
	 * @returns { SelectedUiFilters } -> get selected filters
	 */

	static getSelectedFilters(filters: YourWorkFilters): SelectedUiFilters {
		const selectedUiFilters: SelectedUiFilters = {};
		for (const category of filters) {
			if (
				category.label !== SEARCH_FILTERS_CATEGORY_LABELS.dateModified &&
				category.selectedOptions &&
				category.selectedOptions.size > 0 &&
				!category.selectedOptions.has('all')
			) {
				const categorySelectedFilters = Array.from(category.selectedOptions);
				selectedUiFilters[category.label] = categorySelectedFilters;
			} else if (
				category.label === SEARCH_FILTERS_CATEGORY_LABELS.dateModified &&
				category.selectedOption &&
				category.selectedOption !== 'all'
			) {
				selectedUiFilters[category.label] = category.selectedOption;
			}
		}
		return selectedUiFilters;
	}

	/**
	 * If the host is CCD, then call the host's handleDeepLink function with the deepLinkParams
	 * @param  - `searchHostContext`: The `HostContext` object that was passed to the `Search` component.
	 * @param  -  `deeplinkParams`: the information needed by CCD to route
	 * @returns - boolean indicating the successful routing
	 */
	static async routeDeepLinksInCCD({
		searchHostContext,
		deepLinkParams,
		itemType,
		templateParams,
	}: {
		searchHostContext: HostContext;
		deepLinkParams: DeepLinkParams;
		itemType: string;
		templateParams?: Record<string, any>;
	}): Promise<boolean> {
		switch (searchHostContext.hostInfo.surface.toLowerCase()) {
			case 'ccdesktop': {
				const handleDeepLink = searchHostContext?.handleDeepLink;
				if (!handleDeepLink) {
					return false;
				}

				if (searchHostContext.isCCDComponentVisible) {
					try {
						const pluginsUIEnabled = await searchHostContext.isCCDComponentVisible(
							SEARCH_CCD_COMPONENTS.ccdPlugins,
						);
						if (itemType.toLowerCase() === 'plugins' && pluginsUIEnabled === false) {
							return false;
						}
					} catch (error) {
						getLogger().log(
							'Search Utils:routeDeepLinksInCCD',
							'Error in getting CCD plugins UI visibility',
						);
						return false;
					}
				}

				const deeplinkConfig: DeepLinkParams = { ...deepLinkParams };

				if (deeplinkConfig.template && templateParams && deeplinkConfig.routePath) {
					Object.entries(templateParams).forEach(([key, value]) => {
						const regExp = new RegExp(`{${key}}`, 'ig');
						deeplinkConfig.routePath = deeplinkConfig.routePath.replaceAll(regExp, value);
					});
				}

				searchHostContext.handleDeepLink(deeplinkConfig);
				return true;
			}

			case 'ccweb': {
				const navigateFromWebToCCD = searchHostContext?.navigateFromWebToCCD;
				if (!navigateFromWebToCCD) {
					return false;
				}
				const deeplinkConfig: DeepLinkParams = { ...deepLinkParams };

				if (deeplinkConfig.template && templateParams && deeplinkConfig.routePath) {
					Object.entries(templateParams).forEach(([key, value]) => {
						const regExp = new RegExp(`{${key}}`, 'ig');
						deeplinkConfig.routePath = deeplinkConfig.routePath.replaceAll(regExp, value);
					});
				}

				const routedToCCD = await searchHostContext.navigateFromWebToCCD(deeplinkConfig.routePath);
				return routedToCCD;
			}
			default:
				return false;
		}
	}

	static isProjectRebrandingEnabled(): boolean {
		const searchConfig = SearchConfig.getSearchConfig();
		/**
		 * ccsearch_enable_adobeexpress_rebranding is client side flag for testing purpose
		 * adobeExpressRebranding is a server side flag, on production environment this flag will be used to enable this feature.
		 */
		if (
			!SearchHostContext.hasFeature('ccsearch_enable_projects_rebranding') &&
			(!searchConfig ||
				!searchConfig.ui ||
				!searchConfig.ui.features ||
				!searchConfig.ui.features.projectsRebranding)
		) {
			return false;
		}

		return true;
	}

	/**
	 * It takes in an array of recommendations and an object of app metadata, and returns an array of
	 * recommendation item names
	 * @param {Record<string, any>[]} recommendations - Record<string, any>[] - This is the list of
	 * recommendations that you get from the search API.
	 * @param {AppMetadata} searchTrendingProducts - Array<any> - List of app recommendations from trending API
	 * @returns An array of strings.
	 */
	static getTrendingApps(
		recommendations: Record<string, any>[],
		searchTrendingProducts: Array<any>,
		searchAppMetadataList: AppMetadata,
	) {
		const filteredSearchTrendingProducts = searchTrendingProducts.filter(
			(item) => item in searchAppMetadataList.apps,
		);
		const trendingApps = recommendations.map((recommendation, index): Record<string, string> => {
			let trendingApp = { ...recommendation };
			if (index > 0 && filteredSearchTrendingProducts && filteredSearchTrendingProducts[index - 1]) {
				trendingApp = {
					id: filteredSearchTrendingProducts[index - 1],
					score: index + 1,
					type: 'apps',
					ffcId: filteredSearchTrendingProducts[index - 1],
					action: {
						label: 'Launch',
					},
					recommendationType: 'Suggestions',
				};
			}
			return trendingApp;
		});

		return trendingApps;
	}

	static getCCDCgenParams(appId: string, location: string): { [key: string]: string } {
		const paramsCgen: { [key: string]: string } = {};
		if (['FFC_ccexpress', 'FFC_adobeexpress'].includes(appId)) {
			switch (location) {
				case SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.SEARCH_RESULT:
					paramsCgen.trackingid = SEARCH_CCD_CCH_SAMCAP_CGEN.ccdExpressCgenForSRP;
					break;
				default:
					paramsCgen.trackingid = SEARCH_CCD_CCH_SAMCAP_CGEN.ccdExpressCgenForAppLink;
			}
		}
		return paramsCgen;
	}

	static addCgenParamsForWeb(dataObj: Record<string, any>): string {
		let finalUrl: string = dataObj.applicationLaunchUrl;
		if (
			['FFC_ccexpress', 'FFC_adobeexpress'].includes(dataObj.sapcodeAppId) &&
			dataObj.cliendID === SEARCH_CCD_CCH_SAMCAP_CGEN.cchClient
		) {
			switch (dataObj.appLaunchLocation) {
				case SEARCH_IN_APP_SOURCE_FOR_APP_LAUNCH_ANALYTICS.SEARCH_RESULT:
					finalUrl = finalUrl.includes('?')
						? finalUrl.concat('&', SEARCH_CCD_CCH_SAMCAP_CGEN.cchExpressCgenForSRP)
						: finalUrl.concat('?', SEARCH_CCD_CCH_SAMCAP_CGEN.cchExpressCgenForSRP);
					break;
				default:
					finalUrl = finalUrl.includes('?')
						? finalUrl.concat('&', SEARCH_CCD_CCH_SAMCAP_CGEN.cchExpressCgenForAppLink)
						: finalUrl.concat('?', SEARCH_CCD_CCH_SAMCAP_CGEN.cchExpressCgenForAppLink);
			}
		}
		return finalUrl;
	}

	static initializeSession(): void {
		SearchAnalytics.contextGuid = uuid();
	}

	static isAssetCategoryUIEnabled(
		assetCategory: AssetCategory,
		hostContext: HostContext,
		host?: 'CCDesktop' | 'CCHome' | 'CCAssets',
	): boolean {
		const hostMatch = host ? hostContext?.hostInfo?.client === host : true;
		if (!hostMatch) {
			return true;
		}
		const hostUIHandled =
			hostContext && hostContext.handledResultCategories && Boolean(hostContext.handledResultCategories.length);
		if (hostUIHandled && assetCategory) {
			return hostContext.handledResultCategories
				.map((category) => category.toLowerCase())
				.includes(assetCategory.toLowerCase());
		}
		return false;
	}

	static isCCFilesEOLEnabled(searchHostContext: HostContext = SearchHostContext.getContext()): boolean {
		let ccFilesEOL = true;
		const surface = searchHostContext.hostInfo.client;
		switch (surface) {
			case 'CCDesktop':
				ccFilesEOL = !(searchHostContext?.showCCFilesInSearchResults ?? true);
				break;
			case 'CCAssets':
				ccFilesEOL = SearchHostContext.hasFeature('ccsearch_disable_synced_files_folders');
				break;
			default:
				ccFilesEOL = true;
		}
		return ccFilesEOL;
	}

	// to clear specific data browser storage once CFU clicked
	static async refreshLocalStorage(event) {
		// logging the event for understanding event type('Manual' or '24 Hour')
		getLogger().log('Search Utils', 'refreshing local storage data', event);

		const searchHostContext = SearchHostContext.getContext();
		const configService = new ConfigService(searchHostContext, {});
		const floodgateService = new FloodgateService(searchHostContext);
		const fileTypeService = new FileTypeService(searchHostContext.featureFlags, searchHostContext);

		if (event && event.detail.info === 'Manual') {
			// remove feature flag data from local storage
			floodgateService.deleteFloodgateFeaturesLocalStorage();

			// refetch the feature flag data
			floodgateService.refetchFloodgateFeatures();

			// refetch new config and store it in redux store as well
			configService.refetchConfig(true);

			// fetch SearchAppMeta from server and store it in session storage
			const res: SearchAppMetadata = await SearchUtils.getSearchAppMetadataFromSessionStorage(
				searchHostContext,
				true,
			);
			try {
				store.dispatch({
					type: FETCH_SEARCH_APP_METADATA,
					payload: res,
				});
			} catch (e) {
				getLogger().error('Search Utils', 'Error in storing search app metadata', e.toString());
			}
			// fetch translation data from the server
			fileTypeService.fetchFileTypeData(searchHostContext.locale, true);
		} else {
			if (SearchUtils.isFloodgateFeatureInTest('ccs.localDataRefresh', searchHostContext)) {
				// fetch SearchAppMeta from server and store it in session storage
				const res: SearchAppMetadata = await SearchUtils.getSearchAppMetadataFromSessionStorage(
					searchHostContext,
					true,
				);
				try {
					store.dispatch({
						type: FETCH_SEARCH_APP_METADATA,
						payload: res,
					});
				} catch (e) {
					getLogger().error('Search Utils', 'Error in storing search app metadata', e.toString());
				}
				// fetch translation data from the server
				fileTypeService.fetchFileTypeData(searchHostContext.locale, true);
			}
		}
	}

	static getAssetRendition(renditionUrl: string): Promise<any> {
		const searchHostContext = SearchHostContext.getContext();
		const renditionService = new RenditionService(searchHostContext);
		return renditionService.fetchBinary(renditionUrl);
	}

	static isHostFilesPanelActive() {
		const searchHostContext = SearchHostContext.getContext();
		const hostTab = searchHostContext.getHostUIState?.()?.tab ?? '';
		const hostFilesTabActive = ['files', 'yourwork', 'ccdassets'].includes(hostTab?.toLowerCase());
		return hostFilesTabActive;
	}

	static isSAYTFilesEnabled(): boolean {
		const hostFilesTabActive = this.isHostFilesPanelActive();
		return SearchHostContext.isAuthenticated() && hostFilesTabActive;
	}

	static getRecentFilesFromWebStorage(referSurfaceRecents = true): Array<ResultItem> {
		const hostContext: HostContext = SearchHostContext.getContext();
		const userAdobeId = hostContext.imsInfo?.adobeId;
		const sessionStorage = WebStorage.sessionStorage;
		const env = hostContext.environment;
		const searchClient = hostContext.hostInfo.client;
		const asdeRecentsCacheKey = getConfig(env, 'ASDE_RECENTS_CACHE_KEY');
		const surfaceRecentsCacheKey = getConfig(env, 'SURFACE_RECENTS_CACHE_KEY')?.[searchClient];
		let surfaceRecentFileMap: Record<string, ResultItem> = {};
		let surfaceRecentUpdatedAt;
		if (referSurfaceRecents) {
			const surfaceStorageData = sessionStorage.getItem(surfaceRecentsCacheKey);
			surfaceRecentUpdatedAt = surfaceStorageData?.[userAdobeId]?.cacheTimestamp ?? 0;
			const surfaceRecentFiles = surfaceStorageData?.[userAdobeId]?.data ?? [];
			surfaceRecentFileMap = surfaceRecentFiles
				.filter((file) => file.assetType !== 'tutorial')
				.reduce((acc, file) => {
					const asdeFile = {
						id: file.id,
						assetType: file.assetType,
						attributes: {
							name: file.assetNameExt,
							modificationDate: file.lastModified,
							creationDate: file.lastModified,
							fileSize: file.size,
							mimeType: file.mimeType,
							manifestId: file.manifestId,
							path: file.path,
							ownershipType: file.ownershipType,
							parentId: file.parentId,
							assetCategory: file.source,
							assetSubCategory: file.subSource,
							imageRenditionUrl: file.imageRenditionUrl,
							landingSurface: file.landingSurface,
							openUrl: file.openURI,
							repositoryId: file.repositoryId,
							parentUrl: file.parentUrl,
						},
					};
					acc[file.id] = asdeFile;
					return acc;
				}, {});
		}
		const storageData = sessionStorage.getItem(asdeRecentsCacheKey) as Record<string, any>;
		const asdeRecentsUpdatedAt = storageData?.cacheTimeStamp ?? 0;
		const recentFiles = storageData?.[userAdobeId] ?? [];
		const isSurfaceCacheLatest = surfaceRecentUpdatedAt > asdeRecentsUpdatedAt;
		return isSurfaceCacheLatest && referSurfaceRecents ? Object.values(surfaceRecentFileMap) : recentFiles;
	}

	static setRecentFilesToWebStorage(files: ResultItem[]): void {
		const hostContext: HostContext = SearchHostContext.getContext();
		const userAdobeId = hostContext.imsInfo?.adobeId;
		const sessionStorage = WebStorage.sessionStorage;
		const cacheKey = 'ccs_recentFiles';
		const cacheData = {
			cacheTimeStamp: new Date().getTime(),
			[userAdobeId]: files,
		};
		sessionStorage.setItem(cacheKey, cacheData);
	}

	// get visitor guid
	static async getVisitorGuid(): Promise<string> {
		let visitorGuid;
		try {
			if (window.Visitor) {
				const visitor = window.Visitor.getInstance('D6FAAFAD54CA9F560A4C98A5@AdobeOrg');
				visitorGuid = visitor.getMarketingCloudVisitorID();
			} else if (window.alloy) {
				const identityInfo = await window.alloy('getIdentity');
				visitorGuid = identityInfo.identity.ECID;
			}
		} catch (e) {
			getLogger().error('SearchUtils', 'Error in getting visitor guid', e);
		}
		if (visitorGuid) {
			getLogger().log('SearchUtils', 'ASDE:visitorGuid', visitorGuid);
			return visitorGuid;
		}
		return uuid();
	}

	static getUserIDForStorage(): string {
		return SearchHostContext.getContext().imsInfo.adobeId
			? SearchHostContext.getContext().imsInfo.adobeId
			: STORAGE_CONSTANTS.unAuthKey;
	}
	static sendDelayedSearchAnalytics(query: string): void {
		const analytics = new SearchAnalytics(SearchHostContext.getContext());
		analytics.initSearchAnalytics(query);
		analytics.postSearchDelayedEventFromSRP();
	}
}

SearchUtils.didResetSearchBar = false;
SearchUtils.localFiles = 'localFiles';
SearchUtils.host;
SearchUtils.searchAutoCompleteData = [];
SearchUtils.searchQueryIntentsMap = {};
